import type { ReactNode } from "react";
import { Box } from "@mui/material";
import { StateMessage } from "./StateMessage";

export interface EmptyStateProps {
	/** The noun that is absent: "No job errors". */
	title: string;
	/** Why it is absent: "No jobs match these filters". */
	reason?: ReactNode;
	/** A next step, when the page has one. */
	action?: ReactNode;
}

/** A ring with a slash, drawn rather than imported so it stays neutral: every stock icon that
 *  could stand here reads as either "search" or "success". */
const EMPTY_GLYPH = (
	<Box
		aria-hidden
		sx={{
			width: 36,
			height: 36,
			mb: 0.5,
			borderRadius: "50%",
			color: "text.disabled",
			border: "2px solid currentColor",
			background:
				"linear-gradient(to bottom right, transparent calc(50% - 1px), currentColor calc(50% - 1px), currentColor calc(50% + 1px), transparent calc(50% + 1px))",
		}}
	/>
);

/**
 * A load that succeeded with nothing to show. Neutral on purpose: an empty list is not a
 * success, so it never takes a success colour (docs/design-system.md §4).
 */
export function EmptyState({ title, reason, action }: EmptyStateProps) {
	return <StateMessage role="status" glyph={EMPTY_GLYPH} title={title} detail={reason} action={action} />;
}
